import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, UserPlus, Mail, Lock, User, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACLvHUxODkLRRLjP';

const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Step: 'form' | 'verify'
  const [step, setStep] = useState('form');
  const [email, setEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    pdConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Verification code state
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);
  
  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Load Turnstile script
  useEffect(() => {
    let mounted = true;
    
    const initTurnstile = () => {
      if (mounted && window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        try {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => {
              if (mounted) setTurnstileToken(token);
            },
            'expired-callback': () => {
              if (mounted) setTurnstileToken('');
            },
            'error-callback': (error) => {
              console.log('Turnstile error:', error);
              if (mounted) setTurnstileToken('');
            },
            theme: 'light',
            language: 'ru'
          });
        } catch (e) {
          console.log('Turnstile render error:', e);
        }
      }
    };
    
    // If turnstile already loaded (by Emergent or previous load)
    if (window.turnstile) {
      setTimeout(initTurnstile, 500);
    } else {
      // Load script if not present
      const existingScript = document.querySelector('script[src*="turnstile"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => setTimeout(initTurnstile, 500);
        document.head.appendChild(script);
      } else {
        // Script exists but turnstile not ready yet
        const checkTurnstile = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkTurnstile);
            initTurnstile();
          }
        }, 200);
        setTimeout(() => clearInterval(checkTurnstile), 10000);
      }
    }
    
    return () => {
      mounted = false;
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          // ignore
        }
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  // Re-render turnstile when returning to register step
  useEffect(() => {
    if (step === 'register' && window.turnstile && turnstileRef.current) {
      // Reset widget
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          // ignore
        }
        turnstileWidgetId.current = null;
      }
      setTurnstileToken('');
      
      setTimeout(() => {
        if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
          try {
            turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
              sitekey: TURNSTILE_SITE_KEY,
              callback: (token) => setTurnstileToken(token),
              'expired-callback': () => setTurnstileToken(''),
              theme: 'light',
              language: 'ru'
            });
          } catch (e) {
            console.log('Re-render error:', e);
          }
        }
      }, 300);
    }
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (formData.password.length < 8) {
      setError('Пароль должен быть не менее 8 символов');
      return;
    }

    if (!formData.pdConsent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    // Check Turnstile token
    if (!turnstileToken) {
      setError('Пожалуйста, пройдите проверку CAPTCHA');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          pd_consent: formData.pdConsent,
          turnstile_token: turnstileToken
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // Handle case where response body can't be parsed
        if (response.status === 429) {
          throw new Error('Слишком много запросов. Попробуйте через час.');
        }
        throw new Error('Ошибка сервера. Попробуйте позже.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка регистрации');
      }

      // Move to verification step
      setEmail(formData.email);
      setStep('verify');
      setResendCooldown(60);
      setSuccess('Код подтверждения отправлен на вашу почту');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const codeString = code.join('');
    if (codeString.length !== 6) {
      setError('Введите 6-значный код');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: codeString })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        if (response.status === 429) {
          throw new Error('Слишком много попыток. Попробуйте позже.');
        }
        throw new Error('Ошибка сервера. Попробуйте позже.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка верификации');
      }

      // Success! Save token and redirect
      localStorage.setItem('token', data.access_token);
      window.location.href = '/'; // Full reload to update auth state
      
    } catch (err) {
      setError(err.message);
      setSuccess(''); // Clear success message on error
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        if (response.status === 429) {
          throw new Error('Слишком много запросов. Попробуйте через час.');
        }
        throw new Error('Ошибка сервера. Попробуйте позже.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка отправки кода');
      }

      setSuccess('Новый код отправлен');
      setResendCooldown(60);
      setCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render verification step
  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2 mb-2"
              onClick={() => {
                setStep('form');
                setCode(['', '', '', '', '', '']);
                setError('');
                setSuccess('');
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Назад
            </Button>
            <CardTitle className="text-2xl font-bold text-center">
              Подтверждение email
            </CardTitle>
            <CardDescription className="text-center">
              Введите 6-значный код, отправленный на
              <br />
              <span className="font-medium text-gray-900">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">{success}</AlertDescription>
                </Alert>
              )}

              {/* Code input */}
              <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={el => codeInputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold"
                    disabled={loading}
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>

              {/* Timer */}
              <div className="text-center text-sm text-gray-500">
                Код действителен 10 минут
              </div>

              <Button
                onClick={handleVerify}
                className="w-full"
                disabled={loading || code.join('').length !== 6}
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Проверка...
                  </>
                ) : (
                  'Подтвердить'
                )}
              </Button>

              {/* Resend button */}
              <div className="text-center">
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                  className="text-sm"
                  data-testid="resend-code-btn"
                >
                  {resendCooldown > 0 
                    ? `Отправить код ещё раз (${resendCooldown} сек.)`
                    : 'Отправить код ещё раз'
                  }
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render registration form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Регистрация
          </CardTitle>
          <CardDescription className="text-center">
            Создайте аккаунт для доступа к панели управления маркетплейсами
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name">Имя</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Ваше имя"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={loading}
                  minLength={2}
                  data-testid="register-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={loading}
                  data-testid="register-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Минимум 8 символов"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={loading}
                  minLength={8}
                  data-testid="register-password-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Подтверждение пароля</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Повторите пароль"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  disabled={loading}
                  minLength={8}
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <Checkbox
                id="pdConsent"
                checked={formData.pdConsent}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, pdConsent: checked }))
                }
                disabled={loading}
                data-testid="register-consent-checkbox"
              />
              <Label 
                htmlFor="pdConsent" 
                className="text-sm leading-tight cursor-pointer"
              >
                Я согласен на обработку персональных данных в соответствии с{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank" 
                  className="text-blue-600 hover:underline"
                >
                  Политикой конфиденциальности
                </a>
                {' '}(ФЗ-152)
              </Label>
            </div>
            
            {/* Cloudflare Turnstile CAPTCHA */}
            <div className="flex justify-center">
              <div ref={turnstileRef} data-testid="turnstile-widget"></div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !formData.pdConsent || !turnstileToken}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Регистрация...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Зарегистрироваться
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">
              Уже есть аккаунт?{' '}
            </span>
            <Link 
              to="/login" 
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Войти
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
